import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { camelToDisplay, CMVarAggregationObject, MonthNumberEnum, roundDecimals, } from '@iotv/datamodel-core';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Sparklines, SparklinesBars } from 'react-sparklines';
import { daysInMonthMap } from '../../helpers/format';
const debug = process.env.REACT_APP_DEBUG && false;
const aggregateCMVs = (cmvs) => {
    const cmv = new CMVarAggregationObject();
    cmvs.forEach((v) => {
        cmv.mean = (cmv.mean * cmv.cn + v.mean * v.cn) / (cmv.cn + v.cn);
        cmv.cn += v.cn;
        cmv.firstTimestamp = cmv.firstTimestamp ? (v.firstTimestamp ? Math.min(cmv.firstTimestamp, v.firstTimestamp) : cmv.firstTimestamp) : v.firstTimestamp;
        cmv.lastTimeStamp = cmv.lastTimeStamp ? (v.lastTimeStamp ? Math.max(cmv.lastTimeStamp, v.lastTimeStamp) : cmv.lastTimeStamp) : v.lastTimeStamp;
        cmv.max = Math.max(cmv.max ?? 0, v.max ?? 0);
        cmv.min = Math.min(cmv.min ?? 0, v.min ?? 0);
        cmv.sqDiffFromMean += v.sqDiffFromMean; // THIS IS HIGHLY LIKELY TO BE BOLLOCKS - sqdifffrommean is calculated a bit strangely in the CMVar update method, not entirely sure of the rationale.
        /* It would me possible to keep track of the variance (std^2) over time, but the calculation is predicated on knowing the prior variance and mean.
         * We have the mean, but every CMV with a count higher than 1 can't guarantee a correct variance.
         * ADH (24 July 2024) emailed MJE about this.
         * */
    });
    return cmv;
};
const dayCMVs = (hours) => Object.values(hours).reduce((a, c) => {
    a.push(c);
    return a;
}, []);
const monthCMVs = (days) => Object.values(days).reduce((a, c) => {
    a = [...a, ...dayCMVs(c)];
    return a;
}, []);
const dayCMVsForK = (hours, k) => Object.values(hours).reduce((a, c) => {
    c?.[k] && a.push(c[k]);
    return a;
}, []);
const monthCMVsForK = (days, k) => {
    const m = Object.values(days).reduce((a, c) => {
        a = [...a, ...dayCMVsForK(c, k)];
        return a;
    }, []);
    debug && console.log(`Month CMVs for ${k}`, m);
    return m;
};
const AccumulatorKeySummaryCell = ({ days, cmvKey, month, mode }) => {
    const monthCMVMap = aggregateCMVs(monthCMVsForK(days, cmvKey));
    const stats = {
        mean: Math.round(monthCMVMap.mean),
        stdDev: monthCMVMap.sdSample,
        count: monthCMVMap.cn,
        max: monthCMVMap.max ? Math.round(monthCMVMap.max) : null,
        min: monthCMVMap.min ? Math.round(monthCMVMap.min) : null,
    };
    // Aggregate sparklines data
    let aggregatedData = [];
    if (mode == 'day') {
        for (let i = 0; i < 24; i++) {
            aggregatedData.push(0);
        }
        Object.values(days)
            .slice(0, daysInMonthMap[month])
            .forEach((day) => {
            Object.values(day).forEach((hourCMV, idx) => {
                if (!hourCMV || !hourCMV[cmvKey]?.mean)
                    return;
                //console.log(hourCMV)
                if (!aggregatedData[idx])
                    aggregatedData[idx] = 0;
                aggregatedData[idx] += hourCMV[cmvKey].mean;
            });
        });
    }
    else if (mode == 'month') {
        aggregatedData = Object.values(days)
            .slice(0, daysInMonthMap[month])
            .map((hours) => {
            return Object.values(hours).reduce((monthMean, cmvMap) => {
                if (cmvMap?.[cmvKey]) {
                    monthMean += cmvMap[cmvKey].mean;
                }
                return monthMean;
            }, 0);
        });
    }
    return (_jsxs(Grid, { container: true, sx: { minWidth: 250 }, spacing: 1, children: [_jsx(Grid, { size: { xs: 8 }, children: stats.count > 0 && (_jsx(Sparklines, { data: aggregatedData, height: 130, children: _jsx(SparklinesBars, {}) })) }), _jsx(Grid, { size: { xs: 4 }, sx: { fontSize: 13 }, children: _jsx(Grid, { container: true, children: Object.entries(stats).map(([k, v]) => (_jsxs(_Fragment, { children: [_jsxs(Grid, { size: { xs: 8 }, children: [" ", camelToDisplay(k), " "] }), _jsxs(Grid, { size: { xs: 4 }, children: [" ", v != null && stats.count != 0 ? roundDecimals(v, 2) : '-', " "] })] }))) }) })] }));
};
const AccumlatorMonthRow = ({ keys, monthNumber, days, mode }) => {
    return (_jsxs(TableRow, { style: { borderStyle: 'solid', borderWidth: '1px' }, children: [_jsx(TableCell, { width: 100,
                style: {
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'white',
                    borderRight: 'solid 1px #ebebeb',
                    paddingLeft: '0.5em',
                    paddingRight: '1em',
                },
                children: _jsx("div", { children: Object.values(MonthNumberEnum)[monthNumber] }) }, 'leftKey'), keys.map((k, i) => (_jsx(TableCell, { width: 300, children: _jsx(AccumulatorKeySummaryCell, { cmvKey: k, days, month: monthNumber, mode }) }, `${k}_${i}`)))] }));
};
export const AccumulatorTable = ({ accumulator, mode = 'month' }) => {
    const cmvKeys = Object.keys(accumulator.keyMap ?? {}).sort();
    return (_jsxs(Table, { stickyHeader: true, children: [_jsx(TableHead, { sx: { position: 'sticky', zIndex: 100 }, children: _jsxs(TableRow, { children: [_jsx(TableCell, { width: 20 }), cmvKeys.map((k) => (_jsx(TableCell, { children: _jsx("div", { children: camelToDisplay(k) }) }, k)))] }) }), _jsx(TableBody, { children: accumulator.accumulator &&
                    Object.entries(accumulator.accumulator).map(([monthNumberStr, days], i) => (_jsx(AccumlatorMonthRow, { monthNumber: i, days, keys: cmvKeys, mode }, i))) })] }));
};
